import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { draftConversationReply } from 'data/api/intercom.api';
import { selectAccount } from 'store/accountSlide';

// Server-owned, not a build-time constant: GET /users answers whether this
// user may draft, so changing who has the feature is a config update and a
// restart rather than a frontend rebuild. The endpoint enforces the same
// rule, so hiding the control is presentation, never the gate. Undefined
// while the account is still loading, which reads as off.
export const useAiDraftEnabled = (): boolean =>
  !!useSelector(selectAccount)?.canDraftReply;

// Every piece of draft state carries the member it belongs to, copy status
// included. Clearing on a userId change in an effect is too late: the composer
// re-renders with the new member before effects run, so the previous member's
// draft would be shown once. Ownership is checked at read time instead, and
// every async completion has to prove it is still both current and owned.
interface DraftState {
  owner?: string;
  text: string | null;
  drafting: boolean;
  error: string;
  copied: boolean;
  copyError: string;
}

const EMPTY_DRAFT: DraftState = {
  owner: undefined,
  text: null,
  drafting: false,
  error: '',
  copied: false,
  copyError: '',
};

export const useDraftReply = (userId?: string) => {
  const [state, setState] = useState<DraftState>(EMPTY_DRAFT);
  const copiedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Bumped by anything that supersedes an in-flight operation: closing the
  // panel, a manual edit, or a new request.
  const opId = useRef<number>(0);
  // The member on screen right now. A captured `owner` says who a request was
  // for; this says who is being looked at when it lands. Both must agree.
  const currentUserId = useRef<string | undefined>(userId);
  currentUserId.current = userId;

  useEffect(
    () => () => {
      if (copiedTimer.current) clearTimeout(copiedTimer.current);
    },
    [],
  );

  // Anything belonging to another member reads as absent, with no effect in
  // between. The stale state is dropped on the next write.
  const owned = state.owner && state.owner === userId ? state : EMPTY_DRAFT;

  // A completion may only be applied if nothing superseded it AND the member
  // it was for is still the one on screen. The token alone cannot tell an
  // A -> B -> A sequence apart; the owner alone cannot tell a superseded
  // request from a live one.
  const stillApplies = (token: number, owner: string) =>
    opId.current === token && currentUserId.current === owner;

  const reset = () => {
    opId.current++;
    if (copiedTimer.current) clearTimeout(copiedTimer.current);
    setState(EMPTY_DRAFT);
  };

  const requestDraft = async () => {
    if (!userId) return;
    const owner = userId;
    const token = ++opId.current;
    setState({ ...EMPTY_DRAFT, owner, drafting: true });
    try {
      const res = await draftConversationReply(owner);
      if (!stillApplies(token, owner)) return;
      setState({ ...EMPTY_DRAFT, owner, text: res?.draft || '' });
    } catch (e) {
      if (!stillApplies(token, owner)) return;
      setState({
        ...EMPTY_DRAFT,
        owner,
        error: 'Could not generate a draft. Please try again.',
      });
    }
  };

  const copyDraft = async () => {
    const text = owned.text;
    if (!text || !userId) return;
    const owner = userId;
    const token = ++opId.current;
    try {
      await navigator.clipboard.writeText(text);
    } catch (e) {
      // Denied permission or a non-secure context — keep the draft on screen
      // so the text can still be selected by hand.
      if (!stillApplies(token, owner)) return;
      setState((current) => ({
        ...current,
        copied: false,
        copyError: 'Could not copy. Select the text and copy it manually.',
      }));
      return;
    }
    // The clipboard now holds this member's text. Confirming the copy under a
    // different member would invite pasting it into the wrong conversation.
    if (!stillApplies(token, owner)) return;
    setState((current) => ({
      ...current,
      copied: true,
      copyError: '',
    }));
    if (copiedTimer.current) clearTimeout(copiedTimer.current);
    copiedTimer.current = setTimeout(() => {
      if (currentUserId.current !== owner) return;
      setState((current) =>
        current.owner === owner ? { ...current, copied: false } : current,
      );
    }, 2000);
  };

  // A manual edit supersedes any in-flight draft or copy, so a late response
  // cannot overwrite what was just typed.
  const setDraft = (text: string) => {
    if (!userId) return;
    opId.current++;
    setState({ ...EMPTY_DRAFT, owner: userId, text });
  };

  return {
    draft: owned.text,
    setDraft,
    drafting: owned.drafting,
    draftError: owned.error,
    copied: owned.copied,
    copyError: owned.copyError,
    requestDraft,
    copyDraft,
    reset,
    isOpen: owned.drafting || owned.text !== null || !!owned.error,
  };
};
